import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import LinkedRecordOpen from '../../Record/LinkedRecordOpen';
import Icon from '../../common/UI/Icon';
import Checkbox from '../../common/UI/Checkbox';
import SCENE_CONTAINER from '../../../configs/sceneContainer';
import styles from '../gantt.less';

const DEFAULT_WIDTHS = { id: 60, text: 200, startText: 90, endText: 90 };

/**
 * Колонки грида вместе с их ячейками.
 *
 * Ячейку колонке отдают через поле cell — это штатный способ, в самой библиотеке так
 * сделана колонка удаления. Компонент получает { row, column }, где row — задача.
 */
const useColumns = ({ columnWidths, checked, onToggleRecord, sceneId, catalogId }) => {
  const { t } = useTranslation();

  const IdCell = useCallback(
    ({ row }) => (
      <div className={styles.idCell}>
        <Checkbox className={styles.idCheckbox} checked={checked.has(row.id)} onChange={() => onToggleRecord(row.id)} />
        <Icon type="icon programing-22" className={styles.idIcon} />
        <span className={styles.idText}>{row.id}</span>
        <LinkedRecordOpen
          sceneId={sceneId}
          params={{ catalogId, recordId: row.id }}
          container={SCENE_CONTAINER.POPUP}
          linkProps={{
            title: t('record.openModal'),
            className: styles.openRecord,
            children: <Icon type="icon interface-44" />,
          }}
        />
      </div>
    ),
    [sceneId, catalogId, t, checked, onToggleRecord],
  );

  const DateCell = useCallback(({ row, column }) => {
    const time = column.id === 'startText' ? row.startTime : row.endTime;

    return (
      <span className={styles.dateCell}>
        {row[column.id]}
        {time ? <span className={styles.dateTime}>{time}</span> : null}
      </span>
    );
  }, []);

  return useMemo(() => {
    const width = (id) => (columnWidths && columnWidths[id]) || DEFAULT_WIDTHS[id];

    return [
      { id: 'id', header: t('gantt.columns.id'), width: width('id'), cell: IdCell },
      { id: 'text', header: t('gantt.columns.title'), width: width('text') },
      { id: 'startText', header: t('gantt.columns.start'), width: width('startText'), cell: DateCell },
      { id: 'endText', header: t('gantt.columns.end'), width: width('endText'), flexgrow: 1, cell: DateCell },
    ];
  }, [t, columnWidths, IdCell, DateCell]);
};

export default useColumns;
